import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import { CLASSES } from "../data/classes";
import { ACHIEVEMENTS, RARITY_STYLES, totalAvailablePoints, getRank, RANK_TIERS } from "../data/achievements";
import { CLASS_UNLOCK_LEVEL } from "../data/profile";
import {
  ArrowLeft, Edit2, Check, X, Trophy, Lock, Sparkles, Award, Star,
} from "lucide-react";
import { SFX } from "../utils/audio";
import { BORDERS_BY_ID, BANNERS_BY_ID } from "../data/cosmetics";

const Profile = () => {
  const navigate = useNavigate();
  const { user, loginWithGoogle } = useAuth();
  const { profile, updateProfile } = useProfile();
  const [pickingAvatar, setPickingAvatar] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(profile.display_name || "");
  const [tab, setTab] = useState("achievements");

  if (!profile) return null;

  const rank = getRank(profile.rank_points);
  const unlockedIds = new Set((profile.achievements || []).map((a) => a.id));
  const unlockedCount = unlockedIds.size;
  const totalAch = ACHIEVEMENTS.length;

  const handlePickAvatar = (classeId) => {
    SFX.click();
    updateProfile({ avatar_classe: classeId });
    setPickingAvatar(false);
  };

  const handleSaveName = () => {
    const n = draftName.trim();
    if (!n || n === profile.display_name) {
      setEditingName(false);
      return;
    }
    updateProfile({ display_name: n });
    setEditingName(false);
  };

  const avatarClass = profile.avatar_classe ? CLASSES[profile.avatar_classe] : null;
  const equippedBorder = BORDERS_BY_ID[profile.equipped_border || "default"] || BORDERS_BY_ID.default;
  const equippedBanner = BANNERS_BY_ID[profile.equipped_banner || "default"] || BANNERS_BY_ID.default;

  return (
    <div className="h-screen w-screen bg-slate-900 bg-dots overflow-y-auto">
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <button
            data-testid="back-to-menu-profile"
            onClick={() => navigate("/")}
            className="bg-slate-700 text-white border-4 border-slate-950 rounded-xl px-3 py-2 cartoon-shadow flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Menu
          </button>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">Mon Profil</h1>
          <div className="w-20" />
        </div>

        {/* Hero card */}
        <div data-testid="profile-hero" className={`relative border-4 border-slate-950 rounded-3xl p-4 sm:p-5 cartoon-shadow-lg-static mb-5 overflow-hidden ${equippedBanner.css}`}>
          <div className="absolute inset-0 bg-slate-950/45 pointer-events-none" />
          <div className="relative z-10 flex flex-col sm:flex-row gap-4 items-center sm:items-start">
            {/* Avatar */}
            <button
              data-testid="profile-avatar"
              onClick={() => setPickingAvatar((v) => !v)}
              className={`relative w-28 h-28 sm:w-32 sm:h-32 rounded-2xl overflow-hidden flex-shrink-0 cartoon-shadow-static hover:-translate-y-0.5 transition-transform ${equippedBorder.style.className} ${equippedBorder.style.ring}`}
              title="Changer d'avatar"
            >
              {avatarClass ? (
                <div className={`w-full h-full bg-gradient-to-br ${avatarClass.couleur}`}>
                  <img src={avatarClass.image} alt={avatarClass.nom} className="w-full h-full object-cover" />
                </div>
              ) : profile.picture ? (
                <img src={profile.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-600 flex items-center justify-center text-4xl">
                  ?
                </div>
              )}
              <div className="absolute bottom-1 right-1 bg-yellow-400 text-slate-950 border-2 border-slate-950 rounded-md px-1.5 py-0.5 text-[10px] font-bold flex items-center gap-1">
                <Edit2 className="w-3 h-3" /> Changer
              </div>
            </button>

            {/* Name + Rank + Level */}
            <div className="flex-1 w-full">
              {editingName ? (
                <div className="flex gap-2 mb-2">
                  <input
                    data-testid="display-name-input"
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    maxLength={24}
                    className="flex-1 bg-slate-900 text-white border-2 border-slate-950 rounded-lg px-2 py-1 font-display font-bold"
                  />
                  <button data-testid="save-name-btn" onClick={handleSaveName} className="bg-emerald-500 text-white border-2 border-slate-950 rounded-lg px-2 py-1"><Check className="w-4 h-4" /></button>
                  <button onClick={() => { setEditingName(false); setDraftName(profile.display_name); }} className="bg-rose-500 text-white border-2 border-slate-950 rounded-lg px-2 py-1"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="flex items-center gap-2 mb-2">
                  <h2 data-testid="display-name" className="font-display text-2xl sm:text-3xl font-bold text-white truncate">{profile.display_name}</h2>
                  <button data-testid="edit-name-btn" onClick={() => setEditingName(true)} className="text-slate-400 hover:text-yellow-400" title="Renommer">
                    <Edit2 className="w-4 h-4" />
                  </button>
                </div>
              )}
              {!profile.authed && (
                <p className="text-amber-400 text-xs font-bold mb-2">Mode invité — connecte-toi pour sauvegarder dans le cloud</p>
              )}
              {profile.friend_code && (
                <p className="text-slate-400 text-xs mb-2">Code ami : <span className="font-mono font-bold text-yellow-400">{profile.friend_code}</span></p>
              )}

              {/* Level bar */}
              <div data-testid="level-bar" className="bg-slate-900 border-2 border-slate-950 rounded-xl p-2 mb-2">
                <div className="flex items-center justify-between text-xs font-bold mb-1">
                  <span className="flex items-center gap-1 text-fuchsia-400">
                    <Sparkles className="w-3 h-3" /> NIVEAU <span data-testid="level-number" className="text-white text-base ml-1">{profile.level}</span>
                  </span>
                  <span className="text-slate-400">{profile.currentXP} / {profile.neededXP} XP</span>
                </div>
                <div className="h-2.5 bg-slate-950 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-fuchsia-500 to-purple-500 transition-all" style={{ width: `${profile.progress}%` }} />
                </div>
              </div>

              {/* Rank bar */}
              <div data-testid="rank-bar" className={`bg-gradient-to-r ${rank.current.color} border-2 border-slate-950 rounded-xl p-2`}>
                <div className={`flex items-center justify-between text-xs font-bold ${rank.current.text}`}>
                  <span className="flex items-center gap-1">
                    <Trophy className="w-3 h-3" /> RANG <span className="text-base ml-1">{rank.current.label}</span>
                  </span>
                  <span>{profile.rank_points} pts{rank.next ? ` / ${rank.next.min}` : ""}</span>
                </div>
                <div className="h-1.5 bg-slate-950/40 rounded-full overflow-hidden mt-1">
                  <div className="h-full bg-white/70" style={{ width: `${rank.progress}%` }} />
                </div>
                <p className={`text-[10px] mt-0.5 italic ${rank.current.text} opacity-80`}>Le rang servira pour le mode multijoueur classé.</p>
              </div>
            </div>
          </div>

          {/* Avatar picker drawer */}
          {pickingAvatar && (
            <div data-testid="avatar-picker" className="mt-4 bg-slate-900 border-2 border-slate-950 rounded-2xl p-3">
              <p className="font-display font-bold text-sm mb-2 text-yellow-400">Choisis ton avatar (parmi les 6 classes)</p>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {Object.values(CLASSES).map((c) => {
                  const isCurrent = profile.avatar_classe === c.id;
                  // We allow picking any class as avatar (no unlock gate for cosmetics)
                  return (
                    <button
                      key={c.id}
                      data-testid={`avatar-pick-${c.id}`}
                      onClick={() => handlePickAvatar(c.id)}
                      className={`relative bg-slate-800 border-4 ${isCurrent ? "border-yellow-400" : "border-slate-950"} rounded-xl p-1.5 transition-transform hover:-translate-y-0.5`}
                      title={c.nom}
                    >
                      <div className={`w-full aspect-square rounded-lg bg-gradient-to-br ${c.couleur} border-2 border-slate-950 overflow-hidden`}>
                        <img src={c.image} alt={c.nom} className="w-full h-full object-cover" />
                      </div>
                      <p className="text-[10px] font-bold mt-0.5 leading-tight">{c.nom.replace("Le ", "").replace("L'", "")}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
          <div className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-3 text-center cartoon-shadow-lg-static">
            <Sparkles className="w-5 h-5 mx-auto text-fuchsia-400 mb-1" />
            <p className="text-[10px] text-slate-400 uppercase font-bold">XP total</p>
            <p data-testid="stat-xp-total" className="font-display font-bold text-lg">{profile.xp_total}</p>
          </div>
          <div className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-3 text-center cartoon-shadow-lg-static">
            <Trophy className="w-5 h-5 mx-auto text-yellow-400 mb-1" />
            <p className="text-[10px] text-slate-400 uppercase font-bold">Pts de rang</p>
            <p data-testid="stat-rank-points" className="font-display font-bold text-lg">{profile.rank_points}</p>
          </div>
          <div className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-3 text-center cartoon-shadow-lg-static">
            <Award className="w-5 h-5 mx-auto text-emerald-400 mb-1" />
            <p className="text-[10px] text-slate-400 uppercase font-bold">Succès</p>
            <p data-testid="stat-achievements" className="font-display font-bold text-lg">{unlockedCount} / {totalAch}</p>
          </div>
          <div className="bg-slate-800 border-4 border-slate-950 rounded-2xl p-3 text-center cartoon-shadow-lg-static">
            <Star className="w-5 h-5 mx-auto text-amber-400 mb-1" />
            <p className="text-[10px] text-slate-400 uppercase font-bold">Pts max</p>
            <p className="font-display font-bold text-lg">{totalAvailablePoints}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-3">
          {[
            { id: "achievements", label: `🏆 Succès (${unlockedCount}/${totalAch})` },
            { id: "classes",      label: "🔓 Classes" },
            { id: "ranks",        label: "📈 Rangs" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex-1 sm:flex-none px-3 py-2 rounded-xl border-2 border-slate-950 font-display font-bold text-xs uppercase cartoon-shadow-static ${tab === t.id ? "bg-yellow-400 text-slate-950" : "bg-slate-800 text-slate-300"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "achievements" && (
          <div data-testid="achievements-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 pb-6">
            {ACHIEVEMENTS.map((a) => {
              const unlocked = unlockedIds.has(a.id);
              const style = RARITY_STYLES[a.rarity];
              return (
                <div
                  key={a.id}
                  data-testid={`ach-${a.id}`}
                  className={`relative ${style.bg} border-4 border-slate-950 rounded-2xl p-3 ring-2 ${style.ring} ${unlocked ? "" : "opacity-60 grayscale"}`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-12 h-12 rounded-xl border-2 border-slate-950 flex items-center justify-center text-3xl flex-shrink-0 ${unlocked ? "bg-slate-950/40" : "bg-slate-950/60"}`}>
                      {unlocked ? a.icon : <Lock className="w-5 h-5 text-slate-300" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-bold text-sm leading-tight">{a.name}</p>
                      <p className="text-xs text-slate-300 leading-tight">{a.desc}</p>
                      {a.flavor && <p className="text-[10px] text-slate-400 italic mt-1 leading-tight">« {a.flavor} »</p>}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-yellow-400">{style.label}</span>
                        <span className="text-[10px] font-bold text-emerald-400">+{a.points} pts</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "classes" && (
          <div data-testid="classes-grid" className="grid grid-cols-2 sm:grid-cols-3 gap-2 pb-6">
            {Object.values(CLASSES).map((c) => {
              const unlockLvl = CLASS_UNLOCK_LEVEL[c.id] ?? 1;
              const unlocked = profile.level >= unlockLvl;
              return (
                <div
                  key={c.id}
                  data-testid={`profile-class-${c.id}`}
                  className={`bg-slate-800 border-4 border-slate-950 rounded-2xl p-3 cartoon-shadow-lg-static ${unlocked ? "" : "opacity-70"}`}
                >
                  <div className={`w-full aspect-square rounded-xl bg-gradient-to-br ${c.couleur} border-2 border-slate-950 overflow-hidden mb-2 relative`}>
                    <img src={c.image} alt={c.nom} className={`w-full h-full object-cover ${unlocked ? "" : "grayscale brightness-50"}`} />
                    {!unlocked && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950/60">
                        <Lock className="w-8 h-8 text-yellow-300" />
                        <span className="text-yellow-300 font-bold text-xs mt-1">Niv. {unlockLvl}</span>
                      </div>
                    )}
                  </div>
                  <p className="font-display font-bold text-sm">{c.nom}</p>
                  <p className="text-[10px] text-slate-400 uppercase font-bold">
                    {unlocked ? "✓ Débloquée" : `Débloque au niveau ${unlockLvl}`}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {tab === "ranks" && (
          <div data-testid="ranks-list" className="space-y-2 pb-6">
            {RANK_TIERS.map((t) => {
              const isCurrent = rank.current.id === t.id;
              const reached = profile.rank_points >= t.min;
              return (
                <div
                  key={t.id}
                  className={`bg-gradient-to-r ${t.color} border-4 ${isCurrent ? "border-yellow-300" : "border-slate-950"} rounded-2xl p-3 flex items-center justify-between cartoon-shadow-lg-static ${reached ? "" : "opacity-70"}`}
                >
                  <div className={`flex items-center gap-2 ${t.text}`}>
                    <Trophy className="w-5 h-5" />
                    <span className="font-display font-bold text-base">{t.label}</span>
                    {isCurrent && <span className="bg-slate-950 text-yellow-300 text-[10px] font-bold uppercase rounded px-1.5">Actuel</span>}
                  </div>
                  <div className={`${t.text} font-bold text-sm`}>≥ {t.min} pts</div>
                </div>
              );
            })}
            <p className="text-center text-slate-400 text-xs mt-2">Les points de rang viennent des succès débloqués. Le mode multijoueur classé arrive bientôt !</p>
          </div>
        )}

        {!profile.authed && (
          <div className="mt-2 bg-amber-500/10 border-2 border-amber-500 rounded-2xl p-3 text-center">
            <p className="text-amber-300 text-sm mb-2 font-bold">Sauvegarde-toi pour ne rien perdre.</p>
            <button
              data-testid="profile-login-btn"
              onClick={loginWithGoogle}
              className="bg-white text-slate-950 border-4 border-slate-950 rounded-xl px-4 py-2 font-display font-bold cartoon-shadow"
            >
              Se connecter avec Google
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
